import { useMemo, useState } from "react";
import { useTicket } from "../../../store/Store";
import { adjustmentTypes } from "../../general/constants";
import { useTranslation } from "react-i18next";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import Surcharge from "./Surcharge";
import { capitalize } from "../../general/AppHelpers";
import Currency from "../../general/Currency";
import i18next from "i18next";

const PaymentHistory = () => {
  const { data: ticketData } = useTicket();
  const { resource } = ticketData || {};
  const { adjustments, payment_items } = resource || {};
  const { t } = useTranslation();
  const [openCards, setOpenCards] = useState([]);

  const toggleCard = (cardKey) => {
    setOpenCards((prev) =>
      prev.includes(cardKey)
        ? prev.filter((x) => x !== cardKey)
        : [...prev, cardKey]
    );
  };

  const surchargeAdjustments = useMemo(
    () =>
      adjustments?.filter(
        (a) => a.adjustable_type === adjustmentTypes.surchargeItem && a.id
      ),
    [adjustments]
  );

  const paymentItems = useMemo(() => {
    return payment_items
      ?.map(({ payment }) => {
        const surcharges = surchargeAdjustments?.filter(
          (s) => s.adjustable?.transaction_id === payment.id
        );
        return { ...payment, surcharges };
      })
      .filter((p) => p?.data?.last4 && p?.charged_at); // only valid payments
  }, [payment_items, surchargeAdjustments]);

  const paymentsWithSurcharges = paymentItems?.filter(
    (p) => p.surcharges?.length
  );

  const groupedPayments = useMemo(() => {
    const groups = {};
    paymentsWithSurcharges?.forEach((payment) => {
      const key = `${payment?.data?.funding || "Card"}-${payment?.data?.last4}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(payment);
    });
    return groups;
  }, [paymentsWithSurcharges]);

  if (!paymentsWithSurcharges?.length) return null;

  const renderCardSection = (cardKey, payments) => {
    const isOpen = openCards.includes(cardKey);
    const firstPayment = payments[0];

    const funding = capitalize(firstPayment?.data?.funding || "Card");
    const last4 = firstPayment.data.last4;
    const locale = i18next.language || "en-US";
    const date = new Date(firstPayment.charged_at).toLocaleString(locale, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
    const label = t("payment.card_ending_in", { funding, last4, date });

    const combinedSurcharges = payments.flatMap((p) => p.surcharges || []);
    const surchargeAmount = combinedSurcharges.reduce(
      (sum, s) => sum + Number(s.amount || 0),
      0
    );
    const totalAmount = payments.reduce(
      (sum, p) => sum + Number(p.amount || 0),
      0
    );
    const basePayment = totalAmount - surchargeAmount;

    return (
      <div key={cardKey} className="space-y-1">
        <button
          onClick={() => toggleCard(cardKey)}
          className="flex justify-between items-center w-full text-gray-600 text-sm py-1"
        >
          <span>{label}</span>
          {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </button>

        <div
          className={`transition-all duration-300 ease-in-out text-xs text-gray-500 ${
            isOpen ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
          } overflow-hidden pl-3 space-y-1`}
        >
          <div className="flex justify-between">
            <span>{t("ticket.payment_amount")}:</span>
            <span>
              <Currency value={basePayment} />
            </span>
          </div>

          {combinedSurcharges.map((s) => (
            <Surcharge key={s.id} surcharge={s} />
          ))}

          <div className="flex justify-between font-medium pt-1 border-t border-gray-200">
            <span>{t("ticket.total")}:</span>
            <span className="font-medium">
              <Currency value={totalAmount} />
            </span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-2 text-sm mt-2">
      <h2 className="text-base font-medium text-black mb-2 text-lg">
        {t("paymentHistory.title")}
      </h2>
      {Object.entries(groupedPayments).map(([cardKey, payments]) =>
        renderCardSection(cardKey, payments)
      )}
    </div>
  );
};

export default PaymentHistory;
